/**
 * Pure helpers for «Войти через Telegram» and «Подключить Telegram» through
 * the BOT — since 2026-10-18 the only Telegram door anywhere on the site
 * (owner). The component is `components/telegram-bot-login.tsx`; the server
 * half is `TelegramBotLoginStore` (API) and its bot handler.
 *
 * Nothing in this file talks to the network. It decides HOW the phone is
 * handed to Telegram, how often to poll, how a tapped request survives this
 * page being closed and reopened, and who is allowed to poll it (the wait loop
 * itself is `telegram-bot-login-wait.ts`).
 */
import {
  HANDOFF_DEADLINE_MS,
  INSTAGRAM_HANDOFF_DEADLINE_MS,
  type InAppBrowser,
} from './in-app-browser';

/** `https://t.me/<bot>?start=<param>` — the universal link. */
export function telegramBotLink(bot: string, startParam: string): string {
  return `https://t.me/${encodeURIComponent(bot)}?start=${encodeURIComponent(startParam)}`;
}

/**
 * The Telegram APP on Android, straight from an in-app WebView that will not
 * follow a `t.me` link out of itself. Pinned to `org.telegram.messenger`: an
 * unpinned `tg:` intent would hand a login token to whichever installed app
 * claims the scheme. `S.browser_fallback_url` is the `t.me` link, for a phone
 * without Telegram (or a host app that honours the fallback but not the
 * package). This is the hand-off verified on a Redmi 15C inside Instagram
 * (30.09, 16.10): after Instagram's own «Вы покидаете приложение?» Telegram
 * opens and the sign-in goes through.
 */
export function telegramBotIntentUrl(bot: string, startParam: string): string {
  const domain = encodeURIComponent(bot);
  const start = encodeURIComponent(startParam);
  return (
    `intent://resolve?domain=${domain}&start=${start}` +
    `#Intent;scheme=tg;package=org.telegram.messenger;` +
    `S.browser_fallback_url=${encodeURIComponent(telegramBotLink(bot, startParam))};end`
  );
}

/**
 * How the tap hands this person to Telegram.
 *
 * - An in-app browser ON ANDROID (Instagram, Facebook, Telegram's own WebView —
 *   `detectInAppBrowser`): the pinned Android intent (`telegramBotIntentUrl`)
 *   with `t.me` as its fallback — the path measured to work there, kept as it
 *   was (coordinator, 18.10: do not swap a verified hand-off for an unverified
 *   one). Navigated in THIS page.
 * - Everywhere else the `https://t.me/…` universal link, never `tg://` (owner,
 *   18.10): in THIS page on a phone (`same`, inside the tap, so no popup
 *   blocker can drop it; the OS hands the app link to Telegram), in a new tab
 *   on a computer (`tab` — Telegram Desktop or Web, while this page keeps
 *   polling behind it). A WebView that loads `t.me` itself replaces this page,
 *   and the pending cookie brings the wait back when the person comes back.
 */
export function botHandoff(
  inApp: InAppBrowser,
  bot: string,
  startParam: string,
): { kind: 'same' | 'tab'; url: string } {
  if (inApp.os === 'android' && inApp.app !== null) {
    return { kind: 'same', url: telegramBotIntentUrl(bot, startParam) };
  }
  const url = telegramBotLink(bot, startParam);
  return { kind: inApp.os === 'other' ? 'tab' : 'same', url };
}

/** A computer — the desktop copy («Откройте Telegram и нажмите Start»). */
export function isDesktopHandoff(inApp: InAppBrowser): boolean {
  return inApp.os === 'other';
}

/**
 * How long a `same` hand-off may leave this page in place before the funnel
 * row calls it swallowed (`login_failed{reason:'intent_ignored'}`). Inside
 * Instagram and Facebook the page sits visible and focused behind the app's
 * own «leave the app?» dialog for as long as the person reads it — see
 * `INSTAGRAM_HANDOFF_DEADLINE_MS`. Everywhere else `HANDOFF_DEADLINE_MS`.
 * (The on-screen hint does not wait that long — see `IN_APP_HINT_MS`.)
 */
export function botHandoffDeadlineMs(inApp: InAppBrowser): number {
  return inApp.app === 'instagram' || inApp.app === 'facebook'
    ? INSTAGRAM_HANDOFF_DEADLINE_MS
    : HANDOFF_DEADLINE_MS;
}

/** /login and /register: the page is still visible this long after the tap →
 *  show «Не открылся Telegram?» with «Скопировать ссылку» (owner, 18.10). */
export const IN_APP_HINT_MS = 2_000;

/** How long the server accepts a Start on a request: fifteen minutes. */
export const REQUEST_TTL_MS = 15 * 60_000;
/** How long the page keeps asking after the tap: the request's own lifetime. */
export const POLL_WINDOW_MS = REQUEST_TTL_MS;
/** The first ask, a moment after the tap — nobody gets through Telegram faster. */
export const FIRST_POLL_MS = 2_000;
/**
 * Coming back to the page after the window closed still gets this long: a
 * Start pressed at minute fourteen stays collectable for fifteen more, and
 * only the server knows whether that happened.
 */
export const RETURN_GRACE_MS = 60_000;

/**
 * Every ~2 s while an answer is plausible, slower after that: 2 s for the
 * first minute, 3 s to the second, 5 s to the fifth, 10 s to the end of the
 * window (a page left open for a quarter of an hour must not hammer a shared
 * carrier IP). Failures back off on top (×2, ×4, ×8 … capped at 15 s) so a
 * flaky connection or a 429 is not hammered either.
 */
export function nextPollDelayMs(elapsedMs: number, failures = 0): number {
  const base =
    elapsedMs < 60_000 ? 2_000 : elapsedMs < 120_000 ? 3_000 : elapsedMs < 300_000 ? 5_000 : 10_000;
  if (failures <= 0) return base;
  return Math.min(15_000, base * 2 ** Math.min(failures, 3));
}

/** A request the person has TAPPED — what this page is waiting on. */
export interface PendingBotLogin {
  token: string;
  startParam: string;
  /** Epoch ms when the server stops accepting a Start on the request. */
  expiresAt: number;
  /** Shared with the CAPI leg the server fires if this creates the account. */
  metaEventId: string;
  /** Which surface tapped. Only that surface picks the wait back up with its
   *  own UI; anywhere else `TelegramBotLoginResume` collects it silently. */
  surface: string;
  startedAt: number;
  /** The tapping surface's funnel (`register` → `register_failed`), so a
   *  resumed wait files its failure under the same event. Absent = login. */
  intent?: 'login' | 'register';
}

/**
 * The pending sign-in, in a short-lived FIRST-PARTY cookie (owner, 18.10).
 *
 * A cookie, not `sessionStorage`: «tapped, closed the tab, pressed Start,
 * opened the site again in the same browser» must end signed in, and a closed
 * tab takes its sessionStorage with it. Fifteen minutes from the tap, path `/`,
 * written by this page on `uztender.com` with no Domain — so it never rides to
 * `api.uztender.com`. The token is useless without the httpOnly nonce cookie
 * the API set on this same browser, so a copy here is not a credential on its
 * own.
 *
 * Dropped on logout and on every completed sign-in, whatever the door
 * (`AuthProvider`, security review 18.10): a tap left pending on a shared
 * computer must not be collected for whoever signs in there next.
 */
export const PENDING_COOKIE = 'uzt_tgl_wait';
export const PENDING_COOKIE_MAX_AGE_S = 15 * 60;

function writePendingCookie(value: string, maxAgeS: number): void {
  if (typeof document === 'undefined') return;
  const secure =
    typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${PENDING_COOKIE}=${value}; Max-Age=${maxAgeS}; Path=/; SameSite=Lax${secure}`;
}

function readPendingCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === PENDING_COOKIE) return rest.join('=');
  }
  return null;
}

/** Remember the tapped request for this BROWSER — see `PENDING_COOKIE`. */
export function savePendingBotLogin(pending: PendingBotLogin): void {
  try {
    writePendingCookie(encodeURIComponent(JSON.stringify(pending)), PENDING_COOKIE_MAX_AGE_S);
  } catch {
    /* cookies blocked — the in-memory wait still works while the page lives. */
  }
}

/** The tapped request for `surface`, if it is still alive; clears a dead one. */
export function readPendingBotLogin(surface: string, now = Date.now()): PendingBotLogin | null {
  const p = readAnyPendingBotLogin(now);
  return p && p.surface === surface ? p : null;
}

/**
 * The tapped request of THIS browser, whichever surface tapped it — for
 * `TelegramBotLoginResume`, which collects a sign-in the tapping surface is no
 * longer mounted (or no longer open) to collect. Clears a dead or malformed one.
 */
export function readAnyPendingBotLogin(now = Date.now()): PendingBotLogin | null {
  let raw: string | null = null;
  try {
    raw = readPendingCookie();
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const p = JSON.parse(decodeURIComponent(raw)) as Partial<PendingBotLogin>;
    if (
      typeof p.token !== 'string' ||
      typeof p.startParam !== 'string' ||
      typeof p.expiresAt !== 'number' ||
      typeof p.metaEventId !== 'string' ||
      typeof p.startedAt !== 'number' ||
      typeof p.surface !== 'string' ||
      // The cookie's own Max-Age is the real bound; this covers a browser
      // that kept it anyway (a restored session).
      now >= p.startedAt + PENDING_COOKIE_MAX_AGE_S * 1000
    ) {
      clearPendingBotLogin();
      return null;
    }
    return p as PendingBotLogin;
  } catch {
    clearPendingBotLogin();
    return null;
  }
}

/**
 * Forget the tapped request. With `token`, only if it is still THAT request: a
 * wait that ends must never wipe a newer tap that has replaced it.
 */
export function clearPendingBotLogin(token?: string): void {
  try {
    if (token !== undefined) {
      const raw = readPendingCookie();
      if (!raw) return;
      try {
        if ((JSON.parse(decodeURIComponent(raw)) as Partial<PendingBotLogin>).token !== token) {
          return;
        }
      } catch {
        /* malformed — nobody's request; clear it */
      }
    }
    writePendingCookie('', 0);
  } catch {
    /* nothing stored either */
  }
}

/**
 * Who is polling a tapped request right now — at most ONE loop per token per
 * page.
 *
 * The exchange is single-use: of two loops polling one request, one gets
 * `signed_in` and the other `consumed`. So the surface that tapped (`surface`)
 * and the app-wide resumer (`resume`, see `TelegramBotLoginResume`) take
 * turns: the surface always wins — the resumer steps aside (`yieldTo`) the
 * moment the tapping surface mounts again — and the resumer picks a request up
 * only when nobody holds it.
 */
export interface BotLoginWaitOwner {
  kind: 'surface' | 'resume';
  /** Called when a `surface` takes the request over from this `resume` owner. */
  yieldTo?: () => void;
}

const waits = new Map<string, BotLoginWaitOwner>();
const releaseListeners = new Set<() => void>();

/** Take the wait on `token`; false = somebody else holds it. */
export function claimBotLoginWait(token: string, owner: BotLoginWaitOwner): boolean {
  const current = waits.get(token);
  if (current === owner) return true;
  if (current && !(owner.kind === 'surface' && current.kind === 'resume')) return false;
  waits.set(token, owner);
  current?.yieldTo?.();
  return true;
}

/** Let go of `token` — a no-op unless `owner` still holds it. */
export function releaseBotLoginWait(token: string, owner: BotLoginWaitOwner): void {
  if (waits.get(token) !== owner) return;
  waits.delete(token);
  for (const listener of [...releaseListeners]) listener();
}

/** Told whenever a wait is let go of, so the resumer can look for an orphan. */
export function onBotLoginWaitReleased(listener: () => void): () => void {
  releaseListeners.add(listener);
  return () => {
    releaseListeners.delete(listener);
  };
}

/** Tests only. */
export function resetBotLoginWaits(): void {
  waits.clear();
  releaseListeners.clear();
}

/** A request opened but not yet tapped. */
export interface PreparedBotLogin {
  token: string;
  startParam: string;
  expiresAt: number;
  metaEventId: string;
  /** Epoch ms the request was opened — what `PREPARED_REFRESH_MS` counts from. */
  preparedAt: number;
}

/**
 * One prepared request per identity per page load.
 *
 * The button must be ready BEFORE the tap (an async request inside the tap
 * would move the hand-off outside the user gesture, which in-app WebViews drop
 * silently — see `use-prepared-checkout.ts`), so a request is opened when the
 * button mounts. Surfaces remount (the catalog strip on every catalog visit),
 * and without this each remount would open another. Keyed by who is signed in
 * and where the magic link should land: a request opened as a guest words the
 * bot's answer as a login.
 */
const prepared = new Map<string, PreparedBotLogin>();

/**
 * A page left open renews its request this often (owner, 18.10: «every ~10
 * min»), and on every return to the tab once it is this old — so the button
 * never carries a link the server will call expired. Five of the fifteen
 * minutes are always left.
 */
export const PREPARED_REFRESH_MS = 10 * 60_000;

/** Minimum life left for a prepared request to be handed out at all. */
export const PREPARED_MIN_LIFE_MS = 60_000;

/** Is a prepared request due for renewal? */
export function preparedIsStale(req: PreparedBotLogin, now = Date.now()): boolean {
  return now - req.preparedAt >= PREPARED_REFRESH_MS || req.expiresAt - now < PREPARED_MIN_LIFE_MS;
}

export function peekPreparedBotLogin(key: string, now = Date.now()): PreparedBotLogin | null {
  const hit = prepared.get(key);
  if (!hit) return null;
  if (preparedIsStale(hit, now)) {
    prepared.delete(key);
    return null;
  }
  return hit;
}

export function putPreparedBotLogin(key: string, req: PreparedBotLogin): void {
  prepared.set(key, req);
}

export function dropPreparedBotLogin(key: string): void {
  prepared.delete(key);
}

/** Tests only. */
export function resetPreparedBotLogins(): void {
  prepared.clear();
}
